/**
 * Dropbox Integration Module
 *
 * OAuth authentication and API access for Dropbox
 *
 * @example
 * ```typescript
 * import {
 *   // Auth
 *   useDropboxAuth,
 *   getValidDropboxTokens,
 *   isDropboxConfigured,
 *   exchangeDropboxCodeForTokens,
 *   revokeDropboxTokens,
 *   // API
 *   listDropboxFolder,
 *   listDropboxFolderContinue,
 *   getDropboxFolderInfo,
 *   getDropboxUserInfo,
 *   checkDropboxConnection,
 *   getDropboxShareLink,
 *   // Store
 *   useDropboxStore,
 *   useDropboxConnection,
 *   useDropboxFiles,
 *   // Types
 *   type DropboxTokens,
 *   type DropboxFile,
 *   type DropboxFolder,
 *   type DropboxUserInfo,
 *   type DropboxConnectionState,
 * } from '@/lib/dropbox';
 *
 * // Check if OAuth is configured
 * if (isDropboxConfigured()) {
 *   // Use the OAuth hook to authenticate
 *   const { promptAsync, isConfigured, request, response } = useDropboxAuth();
 *
 *   // Handle OAuth response
 *   if (response?.type === 'success' && request?.codeVerifier) {
 *     const tokens = await exchangeDropboxCodeForTokens(
 *       response.params.code,
 *       request.codeVerifier,
 *       redirectUri
 *     );
 *   }
 *
 *   // List folder contents
 *   const { files, folders, cursor, hasMore } = await listDropboxFolder('');
 *
 *   // Get user info
 *   const userInfo = await getDropboxUserInfo();
 *
 *   // Use the store for state management
 *   const { connectionState, connectedEmail } = useDropboxConnection();
 *   const { files, folders, isLoading, hasMore } = useDropboxFiles();
 * }
 * ```
 */

// Export types
export * from './types';

// Export configuration
export * from './config';

// Export auth functions
export * from './auth';

// Export API functions
export * from './api';

// Export store
export * from './store';
